import { Divider, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextBox from '../../components/TextBox';
import { Apps, Description, Edit, SaveAs, Tag } from '@mui/icons-material';
import AutoCompleteBank from '../../components/AutoCompleteBank';
import CustomAutoComplete from '../../components/CustomAutoComplete';
import ButtonSubmit from '../../components/ButtonSubmit';
import CustomSnackBar from '../../components/CustomSnackBar';
import API from '../../services/api';
import ButtonBack from '../../components/ButtonBack';
import MainText from '../../components/MainText';

export default function EditProduct() {
  const { id } = useParams();
  const uri = `/product/${id}/`;
  const [openSnack, setOpenSnack] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("info");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    category: '',
    category_id: '',
    code: '',
    description: '',
    prformat: '',
    prformat_id: '',
    units: 0,
    supplier_id: 0,
  });

  const [format, setFormat] = useState([]);
  const [cat, setCat] = useState([]);
  const [priceT, setPriceT] = useState([]);

  const handleChange = (name, value) => {
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const getCatalogs = async () => {
    try {
      const { data } = await API.get('catalogs/create_product/');
      setFormat(Array.isArray(data?.formats) ? data.formats : []);
      setCat(Array.isArray(data?.categories) ? data.categories : []);
      setPriceT(Array.isArray(data?.price_types) ? data.price_types : []);
    } catch {
      setFormat([]); setCat([]); setPriceT([]);
    }
  };

  const getData = async () => {
    try {
      const response = await API.get(uri);
      const data = response.data;
      console.log("Datos del producto:", data);  // Depuración

      const categoryId = data?.category?.id || data?.category_id || "";
      const categoryName = data?.category?.name || "";
      const prformatId = data?.prformat?.id || data?.prformat_id || "";
      const prformatName = data?.prformat?.name || "";

      // Actualiza formData
      setFormData({
        category: categoryId || categoryName,
        category_id: categoryId || "",
        code: data.code,
        description: data.description,
        prformat: prformatId || prformatName,
        prformat_id: prformatId || "",
        units: data.units,
        supplier_id: data.supplier?.id || data.supplier_id || 0,
      });

    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCatalogs().then(() => getData());
  }, [id]);

  useEffect(() => {
    console.log("Form data:", formData);
  }, [formData]);

  const selectedFormat = useMemo(() => {
    const val = formData.prformat_id || formData.prformat;
    if (!val) return null;
    return format.find(f =>
      String(f.id) === String(val) ||
      (f.name || '').trim().toLowerCase() === String(val).trim().toLowerCase()
    ) || null;
  }, [format, formData.prformat, formData.prformat_id]);

  const selectedCategory = useMemo(() => {
    const val = formData.category_id || formData.category;
    if (!val) return null;
    return cat.find(c =>
      String(c.id) === String(val) ||
      (c.name || '').trim().toLowerCase() === String(val).trim().toLowerCase()
    ) || null;
  }, [cat, formData.category, formData.category_id]);

  const handleSelectChange = (name, value) => {
    setFormData(prevState => ({
      ...prevState,
      [name]: value?.id || value?.name || '',
      ...(name === 'prformat' ? { prformat_id: value?.id || value?.name || '' } : {}),
      ...(name === 'category' ? { category_id: value?.id || value?.name || '' } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await API.patch(uri, formData);

      setOpenSnack(true);
      setSnackMessage("¡Actualizado con éxito!");
      setSnackSeverity("success");
      setTimeout(() => {
        setLoading(false);
        navigate('/orders/product/all-products');
      }, 1500);

    } catch (error) {
      setOpenSnack(true);
      console.log(error);
      setSnackMessage("Intente de nuevo");
      setSnackSeverity("error");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <MainText
      title={'Editar producto'}
      subtitle={'Modifica la información'}
      icon={SaveAs}
      />
      <TextBox
        label='Codigo'
        icon={Tag}
        required
        maxLength={10}
        name={'code'}
        errorMessage='Campo requerido'
        onChange={handleChange}
        type='text'
        newValue={formData.code}
      />
      <TextBox
        label='Descripción'
        icon={Description}
        fullWidth
        required
        maxLength={40}
        name={'description'}
        errorMessage='Campo requerido'
        onChange={handleChange}
        type='text'
        newValue={formData.description}
      />
      <TextBox
        label='Unidades'
        icon={Apps}
        type='number'
        required
        maxLength={10}
        name={'units'}
        errorMessage='Campo requerido'
        onChange={handleChange}
        newValue={formData.units}
      />
      <AutoCompleteBank
        label='Formato'
        options={format}
        getOptionLabel={(format) => format.name}
        onChange={(event, newValue) => handleSelectChange('prformat', newValue)}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id || value)}
        name={'prformat'}
        value={selectedFormat}
      />
      <AutoCompleteBank
        label='Categoria'
        options={cat}
        getOptionLabel={(cat) => cat.name}
        onChange={(event, newValue) => handleSelectChange('category', newValue)}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id || value)}
        name={'category'}
        value={selectedCategory}
      />
      <CustomAutoComplete
        name={'supplier_id'}
        onChange={(name, value) => handleChange('supplier_id', value.id)}
        supplier={formData.supplier_id}
      />
      <Stack spacing={2} direction={"row"}>
        <ButtonSubmit name='Actualizar' icon={Edit} loading={loading} />
        <ButtonBack />
      </Stack>
      {openSnack && (
        <CustomSnackBar open={openSnack} setOpen={setOpenSnack} message={snackMessage} severity={snackSeverity} />
      )}
    </form>
  );
}
